// BOOK TRADE — batch 3's rebuild of NewTrade (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// THE OPERATOR'S RULING (26 Oct 2026, "simplified or closer to that version"): Astra's mockup is the layout and the
// amount shown. One panel, two columns from lg — Party | the direction (Buys / Sells ‹coin›, the customer's side: 2.2);
// Customer pays | the receiving account; Customer receives | the paying account; Rate | Settlement (Settle now / Settle
// later, only where canSettleLater allows: 4.1); then More (When, Notes); then the compact readback and Review trade.
// Review shows the trade once more beside the one irreversible Book trade (Astra's "Review trade" primary). On the
// laptop the aside holds the Last 10 and About this job (the Agreement block, which repeated the fields, went 1 Nov 2026).
//
// THE STATE MACHINE IS NEWTRADE'S, COPIED VERBATIM (the block between the two "verbatim" marks below): the reads and
// their keys, readState on every read, the direction and settle-later defaults (once per party pick), the rails from
// the URL (?dir ?party ?rail ?railAsset ?railSide ?takeAsset ?giveAsset ?take ?give ?lock=coin ?when ?note ?later), the
// three-way calculator, the band and conversion confirms, the deal id minted once and held across a retry, the repeat
// guard and its confirm set, lazy party creation (409 needs_confirm), the new-rail door, the dating nudge, the booked
// readback (deduped said as already booked) and every disabled-until rule. Only the drawing below it is new.
// Not drawn any more: ScreenHeader (PageHead). The Last 10 is back beside the form (LastTen, restored 1 Nov 2026).
import { useState, useMemo, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { ApiError, explain, isSentinel, nothingLanded, readWords } from '../../lib/errors';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../../components/RepeatPanel';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Field } from '../../components/Field';
import { ErrorLine, WarnLine } from '../../components/ErrorLine';
import { AccountsDown } from '../../components/kit';
import { tg } from '../../lib/telegram';
import { derivePaymentMethod, DEFAULT_RAILS, isFiat, ASSETS } from '../../lib/workflow';
import { Combobox, type ComboboxOption, NO_PARTY_CREATE_HINT } from '../../components/Combobox';
import { rosterCut } from '../../lib/roster-cut';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { AssetPicker } from '../../components/AssetPicker';
import { AsOfChips } from '../../components/AsOfChips';
import { MoreDetails } from '../../components/MoreDetails';
import { AboutJob } from '../../components/v2/move/FormFrame';
import { displayAsset, dubaiDate, fmtDay, dubaiWallToIso, isoToDubaiWall, backdateProblem, fmtAmount, fmtRate, shownAmount } from '../../lib/format';
import { partyName, partySubtitle, isStale, needsNameReview, sameAsset, partyBoxWords, foldAsset, dustFor, railName, deskHolds } from '../../lib/naming';
import { useTradeMath } from '../../hooks/useTradeMath';
import { rateProblem } from '../../lib/amount';
import { bandWords, bandFor } from '../../lib/trade-math';
import { readState } from '../../lib/read-state';
import { partyKeyOf, shouldClearSettleLater, conversionGateOf } from '../../lib/settle-later';
import { TradeChargeLinks, TradeReadback, type Booked } from '../NewTrade';
import { Receipt, ReceiptAmount, postedAsOf } from '../../components/v2/Receipt';
import { LastTen, LastTenLink, type LastTenProps } from '../../components/v2/record/LastTen';
import { PageHead, Panel, Notice, StateLine, ServerLine, TAP } from '../../components/v2/kit';
import {
  TradedBox, legWords, hintWords, isNewRailRefusal, newRailHolds, LazyChoiceButton, AmountBox, Toggle,
} from '../../components/v2/book/parts';

type Direction = 'customer_buy' | 'customer_sell';
type Entity = 'dubaiotc' | 'indiaotc';

export function BookTradeV2() {
  // ── verbatim from screens/NewTrade.tsx (NewTrade's body, its state machine) ─────────────────────────────────────
  const nav = useNavigate();

  // One desk. The entity state and the `me` query that once fed a desk switch
  // were dead — no switch is rendered (§5.1, 14 Sept 2026).
  const entity: Entity = 'dubaiotc';
  const [search] = useSearchParams();
  // Arriving from the Migrate checklist with a legacy row in hand.
  //
  // The screen used to send the operator away to book, then back to type the
  // deal id from memory — which is where "DEAL BOOKED" came from. Now the row
  // carries its own facts across: who, which way, and the two AMOUNTS.
  //
  // Amounts and not the rate, deliberately: legacy's displayed rate is rounded
  // and re-deriving a total from it produced 2,116,623.60 against a true
  // 2,116,624.00. The rate box fills itself from the two amounts.
  //
  // What is NOT prefilled is the two rails. Which pot the money came from is the
  // operator's judgment, and the currency guard catches a wrong one — whereas a
  // guessed rail would look authoritative and be silently wrong.
  const [direction, setDirection] = useState<Direction>(
    search.get('dir') === 'customer_sell' ? 'customer_sell' : 'customer_buy');
  // THE TOGGLE OPENS ON THE LAST DIRECTION THE DESK BOOKED WITH THIS PARTY
  // (operator's answer, 14 Sept 2026, to sweep item 14 — not "unpicked"). It
  // is a default, not a rule: the moment the operator touches the toggle, or
  // a link arrives with ?dir= (a chain row, a party page), the party's history
  // is not consulted again for this visit. The readback line above Book says
  // the shape in words either way.
  const dirTouched = useRef(search.has('dir'));
  const pickDirection = (d: Direction) => { dirTouched.current = true; setDirection(d); };
  // ?party=<slug> from a party page or the Migrate checklist.
  const [partySlug, setPartySlug] = useState(search.get('party') ?? '');
  // THE PARTY'S IDENTITY, as the books read it: the BFF trims and lowercases party_slug before it looks the party up (routes/trades.ts), so
  // "hartley-trading ", "HARTLEY-TRADING" and "hartley-trading" are ONE party, booked to one account. Every comparison, query key and read
  // below goes through this — never the raw text of the box, which keeps what was typed (a guard keyed to the raw text lets a space or a
  // capital letter walk round it).
  const partyKey = partyKeyOf(partySlug);

  // Each side is ONE choice (rail = treasury slug + asset together).
  // Initial defaults reflect the 95% case: customer brings AED cash, takes USDT-TRC.
  // When operator flips direction below, both rails swap automatically.
  // Arriving from a Chain row (?rail=&railAsset=): the wallet that moved IS
  // the crypto side of this trade — desk buys → coins arrived (take side of a
  // customer_sell), desk sells → coins left (give side of a customer_buy).
  const chainRail: RailValue | null =
    search.get('rail') && search.get('railAsset')
      ? { slug: search.get('rail')!, asset: search.get('railAsset')! } : null;
  // Without a chain rail the defaults already follow ?dir= on the FIRST render
  // (12 Sept 2026). The direction effect below sets the same rails a frame
  // later anyway; but the calculator seats a prefilled ?take= / ?give= amount
  // by ASSET, so the box it lands in must be the box the asset ends up in —
  // not the pre-swap default it would have had for one frame.
  const urlSell = search.get('dir') === 'customer_sell';
  // WHICH SIDE THE URL'S RAIL BELONGS ON. A chain row says nothing but its
  // direction, and the guess from that — the wallet is the coin side, so the
  // take side of a sell and the give side of a buy — was right for every
  // wallet and wrong for every currency, which is why Record money's links
  // could only carry a coin's rail (14 Sept 2026, sweep items 29 and 39).
  // ?railSide=take|give (Record money sends it: take when the party paid the
  // desk, give when the desk paid the party) names the side outright; absent,
  // the direction-derived guess stands, so every chain link reads as before.
  const railSideParam = search.get('railSide');
  const railOnTake = chainRail !== null
    && (railSideParam === 'take' || railSideParam === 'give' ? railSideParam === 'take' : urlSell);
  // ?takeAsset / ?giveAsset (the deal panel's "Book it again the other way"
  // link, 14 Sept 2026) name the assets of a settle-later rebook outright.
  const urlTakeAsset = search.get('takeAsset'), urlGiveAsset = search.get('giveAsset');
  const [takeRail, setTakeRail] = useState<RailValue>(
    chainRail && railOnTake ? chainRail
      : urlTakeAsset ? { slug: '', asset: urlTakeAsset }
      : { ...(urlSell ? DEFAULT_RAILS.hotTrc : DEFAULT_RAILS.cash) });
  // The OTHER side of a chain-row booking takes the direction's own default —
  // the money side is cash, the coin side the hot wallet. Before 12 Sept 2026
  // a coins-IN row (customer sells) put the wallet on the take side and left
  // the give side on its usual hot-wallet default, so the screen opened with
  // Hot wallet · TRC on BOTH sides and a same-account error.
  const [giveRail, setGiveRail] = useState<RailValue>(
    chainRail && !railOnTake ? chainRail
      : urlGiveAsset ? { slug: '', asset: urlGiveAsset }
      : { ...(urlSell ? DEFAULT_RAILS.cash : DEFAULT_RAILS.hotTrc) });
  // ?lock=coin (a Chain row's booking, sweep item 26, 14 Sept 2026): the coin
  // box shows what the chain moved and cannot be typed over; total and rate
  // derive from it. The chain is the fact; the trade is booked to it.
  const lockCoin = search.get('lock') === 'coin' && chainRail !== null && !isFiat(chainRail.asset);
  const lockedSide: 'take' | 'give' | null = lockCoin ? (railOnTake ? 'take' : 'give') : null;
  // THE CHAIN ROW'S OWN DIRECTION (sweep item 16, 14 Sept 2026): coins IN to
  // the wallet is a customer selling, coins OUT a customer buying. The toggle
  // may still be flipped — nothing here flips it back — but the amber line
  // below the toggle says what the chain row is while it disagrees. A
  // currency rail from a Record money link is not a chain row, so it says
  // nothing about coins in or out.
  const chainDir: Direction | null = chainRail && !isFiat(chainRail.asset)
    ? (railOnTake ? 'customer_sell' : 'customer_buy') : null;

  // THE THREE-WAY CALCULATOR. Operator, 12 Sept 2026: "screens everywhere for
  // asset-1, asset-2 & rate and 3-way auto calculation, that too dynamic which
  // changes with each number I change in the 2nd box" — and "even if I
  // directly paste the rates, total should be there". The shared hook owns the
  // two amounts and the rate: any two fill the third on every keystroke or
  // paste, the rate reads quote per base by the PAIR (AED per USDT, INR per
  // AED, AED per EUR) whichever way the trade goes, an amount outranks the
  // rate (correcting a total re-derives the rate, never the coins — operator,
  // 11 Sept), and a rail switch drops the rate (the 2026-08-22 case: an
  // AED-era 0.0100 survived a switch to the Angadiya INR rail and turned a
  // 498 USDT purchase into "4.98 INR"). Rules in lib/trade-math.ts; nothing
  // here re-implements them.
  const math = useTradeMath(takeRail.asset, giveRail.asset);
  // Read-only views for validation, the payload and the Book button. The
  // boxes themselves read and write the hook directly.
  const amountTake = math.amountOf(takeRail.asset);
  const amountGive = math.amountOf(giveRail.asset);
  const rate = math.rate;
  // ?take= / ?give= (a Chain row's coins) go in as if typed FIRST, so a rate
  // pasted afterwards moves the other box and the coins stay (operator,
  // 1 Sept — "amount × rate = total, any 2 = autofill 3rd"). Mount only: the
  // URL does not change while the screen is open.
  useEffect(() => {
    const give = search.get('give'), take = search.get('take');
    if (give) math.preset({ asset: giveRail.asset }, give);
    if (take) math.preset({ asset: takeRail.asset }, take);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const [notes, setNotes] = useState(search.get('note') ?? '');
  // Which amount box has the cursor — a box the calculator filled shows its figure grouped, four places at most
  // (shownAmount, display only); the one being edited shows the raw string, so a formatter never fights the typing.
  const [editing, setEditing] = useState<'take' | 'give' | null>(null);
  // PICKING A SIDE CHANGES THAT SIDE ONLY. Two automatic behaviours were
  // tried and withdrawn on 12 Sept 2026 — the other side snapping to a
  // counter-asset, and the direction (or the rails) following the coin. The
  // operator's ruling, end of day: "revert this to how it was, I am ok with
  // the same asset selection and will change manually." The same-asset error
  // line and the books' own refusal remain the guards.

  // ── SETTLE LATER — MERCHANT ONLY (operator ruling 2026-08-25) ─────────────
  // "Agreed, nothing moved yet" is this desk's normal merchant shape: HK's INR
  // lands on the day and the USDT follows, in tranches, sometimes days later.
  // Legacy proves it is the rule rather than the exception — every merchant
  // carries 2.4x to 9x more money-movements than trades, so no merchant trade
  // was ever a single self-settling row.
  //
  // It is deliberately NOT offered for customers. A walk-in trade that settles
  // on the spot has no use for it, and an accidental tick would leave a
  // customer carrying a position nobody chases. The control does not exist
  // unless the selected party is a merchant.
  const [settleLater, setSettleLater] = useState(search.get('later') === '1');
  // THE SHAPE A DOOR IS ABOUT TO SEND, read in one place for the payload (buildBody) and for the booking gate (`book`): the switch, or the
  // repeat panel's remedy ("Book it settle-later instead") riding in the submit's variables. The switch alone misses the second.
  const laterFor = (choice?: { kind: string }) => settleLater || choice?.kind === 'settle_later';
  // TICKED BY DEFAULT FOR A MERCHANT (§5.1, 14 Sept 2026: 57 of 72 merchant
  // trades were settle-later, 2 of 49 customer trades). A default, like the
  // direction: applied once per party pick, never after the operator has
  // touched the switch or a link carried ?later=.
  // A CHAIN RAIL ON THE URL DECLARES THE SHAPE TOO (review, 14 Sept 2026):
  // a Chain row's link (?rail=&railAsset=&lock=coin, no ?party, no ?later)
  // says coins moved through that wallet, so the trade is booked settled to
  // it. Before this line the merchant default fired when the operator picked
  // a merchant, the rails effect then dropped the wallet's slug and the
  // payload went out unsettled with no treasury leg — the chain row could
  // not match it, and unticking landed on the TRC default, not the wallet
  // (the 3 Sept ERC-row-on-TRC path). Record money's links send ?later=1
  // alongside the rail, so they read exactly as before.
  const laterTouched = useRef(search.has('later') || chainRail !== null);
  const laterDefaultedFor = useRef<string | null>(null);

  // ── AS OF — WHEN DID THIS HAPPEN? (5 Sept 2026) ───────────────────────────
  // Loki OTC's 3 Sept trade (62,449 USDT at 3.675 for 229,500 AED) was never
  // booked: both money legs were recorded that day, but this screen could only
  // say "now", so the trade would have landed on the 5th — which the sweep
  // cannot settle from 3 Sept money — or been booked settled and double-booked
  // the money. Empty means now (every walk-in). Set, it is the instant the
  // books record, for BOTH shapes. The value is Dubai wall-clock, read through
  // lib/format.ts: a datetime-local carries no zone, and the books have one.
  // Since 14 Sept 2026 the choice is explicit — Now · Yesterday · Pick a time
  // (components/AsOfChips.tsx) — so an omitted date is a chip the operator
  // saw, not a box they skipped (sweep item 23).
  const [when, setWhen] = useState<string>(search.get('when') ?? '');   // ?when=2026-09-03T18:03 prefills the instant (Dubai wall-clock), like ?take/?give
  // The time a Chain row (or a Record money link) sent along: shown as its own chip.
  const urlWhen = search.get('when') ?? '';
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // WHAT THE LAST BOOKING SAID, kept apart from the live field (review, 5 Sept
  // 2026). The success line read `whenIso`, and nothing cleared `when` on
  // success — so after Loki's 3 Sept trade a walk-in typed straight after went
  // out dated 3 Sept as well, and the sweep pooled 3 Sept money for it. The
  // date now leaves with the trade it was typed for (onSuccess), and this
  // holds what was actually POSTED: the instant, the id, the trade in words
  // (the figures clear on success, so the line cannot read the boxes), and
  // whether the BFF deduped it — a retry whose first attempt had landed keeps
  // that attempt's date, whatever this request said.
  const [booked, setBooked] = useState<Booked | null>(null);
  /** The two legs as POSTED, for the receipt's figures (mono, the unit muted beside it). */
  const [bookedLegs, setBookedLegs] = useState<{ pays: { asset: string; amount: number }; gets: { asset: string; amount: number } } | null>(null);

  // Data — fetch a base list of recent customers/merchants on mount so the
  // dropdown is populated the moment the operator focuses the input. As they
  // type, the BFF re-queries with the search term.
  const parties = useQuery({
    queryKey: ['parties', entity, partySlug.trim()],
    queryFn:  () => api.listParties({ entity, q: partySlug.trim() || undefined, with_balances: true }),
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  // WHAT EACH READ IS WORTH — `readState`, said in ONE place (lib/read-state.ts; design system Appendix B): 'known' (a read that
  // SUCCEEDED), 'waiting' (nothing yet: a first read, the retry of a failure, an offline pause) or 'failed' (an error — a failed REFRESH
  // keeps its old rows beside it). A guard needs 'known'; a banner says 'failed' with its Try again and 'waiting' with a short reading
  // line; nothing is claimed from either (no "square", no "no such party", no clear to book).
  // The typed name's search: until it is RESOLVED nobody knows whether this is a party the books hold or a new name.
  const lookup = readState(parties);
  // …and its balances: a list the BFF flags `balances_read: false` has the names and not the balances (its empty balances are not "square").
  const listBalances = readState(parties, { balances: true });
  const accounts = readState(treasury);
  // The picker's box words take the balances' answer.
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry, whatever is typed — the list on screen is one query per search, so the flag is read
  // off the list that is showing, a party picked or not (Astra, 23 Oct 2026: the words went and nothing said why).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  // Customer Buys / Sells = trades WITH a customer or peer counterparty —
  // own_account treasuries must NOT appear in this autocomplete.
  const partyOptions: ComboboxOption[] = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account')
      .slice(0, 20)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        // who they are AND what stands between you — the identifier that
        // matters at a money desk (operator, 2 Sept)
        secondary: [partySubtitle(p, { compact: true }), listBalancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, listBalancesRead]);

  // Existing balance for the selected party — only fetch when the typed slug
  // matches a known party (skips during lazy-create flow when operator is
  // typing a brand-new name).
  const partyExists = useMemo(
    () => (parties.data?.parties ?? []).some((p) => p.slug === partyKey),
    [parties.data, partyKey],
  );
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === partyKey) ?? null,
    [parties.data, partyKey],
  );
  // THE NEAREST EXISTING NAMES (§5.1, 14 Sept 2026): what the books found for
  // the typed text, offered as "Did you mean …?" before the create-as buttons
  // — a duplicate party is one careless tap otherwise.
  const nearest = useMemo(
    () => partyOptions.filter((o) => o.value !== partyKey).slice(0, 3),
    [partyOptions, partyKey],
  );

  // THE PARTY'S RAILS AND LAST DIRECTION, in the one round trip RailPicker
  // already makes (same query key, so react-query asks once and both read
  // the answer). `last_direction` is the latest non-voided deal with this
  // party; a party with no trade yet opens on Customer buys.
  const partyRails = useQuery({
    queryKey: ['party', partyKey, 'rails', entity],
    queryFn:  () => api.partyRails(partyKey, entity),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // ONCE PER PARTY PICK, not on every refetch: the rails answer refreshes on
  // window focus and after each posting, and re-applying the default then
  // would move a toggle the operator had set (review, 14 Sept 2026).
  const dirDefaultedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!partyRails.data || dirTouched.current) return;
    if (dirDefaultedFor.current === partyKey) return;
    dirDefaultedFor.current = partyKey;
    setDirection(partyRails.data.last_direction ?? 'customer_buy');
  }, [partyRails.data, partyKey]);

  // A party's kind cannot change while this form is open, so every merchant we
  // have seen is remembered. Without that the flag would blink: the parties
  // query is re-keyed on the search text, so `data` is undefined for the length
  // of each refetch — long enough to hide the toggle and silently clear it
  // under an operator who had already ticked it.
  const merchantSlugs = useRef<Set<string>>(new Set());
  const isMerchant = useMemo(() => {
    for (const p of parties.data?.parties ?? []) {
      if (p.counterparty_role === 'merchant' || p.party_type === 'counterparty') {
        merchantSlugs.current.add(p.slug);
      }
    }
    return !!partyKey && merchantSlugs.current.has(partyKey);
  }, [parties.data, partyKey]);

  // The settle-later default, once the party is KNOWN (partyExists reads the
  // same answer isMerchant does, so the two agree): a merchant opens ticked,
  // a customer unticked. Skipped for this visit once the switch was touched.
  useEffect(() => {
    if (!partyExists || laterTouched.current) return;
    if (laterDefaultedFor.current === partyKey) return;
    laterDefaultedFor.current = partyKey;
    setSettleLater(isMerchant);
  }, [partyExists, isMerchant, partyKey]);

  const partyBalances = useQuery({
    queryKey: ['party', partyKey, 'balances'],
    queryFn:  () => api.partyDetail(partyKey, entity),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // THE PARTY'S BOX is KNOWN only once a read has SUCCEEDED. A read that failed keeps its old balances beside the error (a failed
  // REFRESH), and a retry of a first failure is PENDING with nothing at all — neither is "square" and neither is what it last said; a
  // paused (offline) read is the same. Every line drawn FROM the box — the box line under the party, the conversion check below — takes
  // this one answer: the figures only when 'known'.
  const box = readState(partyBalances);
  const boxBalances = box === 'known' ? partyBalances.data?.balances : undefined;
  // The ONE reader of the last-known box that stays: who may settle later is a permission, not a line — the switch is not a claim,
  // and going dark on a failed refresh would untick it under the operator and turn a settle-later trade into a settled one.
  const balanceEntries = useMemo(() => {
    const map = partyBalances.data?.balances ?? {};
    return Object.entries(map).filter(([, v]) => v !== 0);
  }, [partyBalances.data]);

  // WHO MAY SETTLE LATER — merchants, and anyone already carrying a position.
  //
  // The rule used to be merchants only, on the reasoning that a walk-in settles
  // on the spot. That is true of a walk-in and false of the books: 45 customers
  // carry running balances, and Kevin — a customer — holds an open unsettled
  // trade booked through ingestion, which never passed this gate. So the console
  // was asserting something the ledger had already contradicted.
  //
  // Operator ruling 2026-08-28: relax it for a customer who already has an open
  // balance. The real distinction was never customer-versus-merchant, it is
  // whether this party carries a position — and a party with a non-zero balance
  // demonstrably does. A party at zero still cannot, which keeps the guard that
  // matters: an ordinary walk-in cannot accidentally book a trade that moves
  // nothing.
  const hasOpenPosition = balanceEntries.length > 0;
  const canSettleLater = isMerchant || hasOpenPosition;
  // Only once the party is KNOWN: on mount the parties list is still loading
  // and this used to switch settle-later off under a ?later=1 link before
  // the merchant had been seen (review, 14 Sept 2026). And only on a KNOWN
  // ineligibility (shouldClearSettleLater): while the box is still being read
  // `hasOpenPosition` is false for a customer who carries a position, and
  // clearing the switch then turned the trade into a settled one — rails
  // restored, the conversion guard walked round — before the box could say
  // (Astra, 23 Oct 2026). While either read waits or failed the switch stays
  // as the operator, or the link, set it.
  useEffect(() => {
    if (shouldClearSettleLater({ partyExists, lookup, box, canSettleLater })) setSettleLater(false);
  }, [partyExists, lookup, box, canSettleLater]);

  // Direction toggle swaps rails to the 95% defaults for that direction.
  // customer_buy : take = cash AED  (customer pays)   · give = hot TRC USDT (desk gives)
  // customer_sell: take = hot TRC USDT (customer pays)· give = cash AED     (desk gives)
  //
  // `settleLater` is a dependency for the same reason direction is: it changes
  // what a leg MEANS. Unsettled, only the asset survives — there is no drawer
  // to name — and turning it back off restores the real default rails, so a
  // cleared slug can never be left behind for the payload to inherit.
  // A CHAIN CHIP'S WALLET MUST SURVIVE THE FIRST RENDER (operator, 3 Sept:
  // booked an 18,345 USDT·ERC sale from the ERC row and it landed on the TRC
  // wallet). This effect also fires on mount, and it overwrote the wallet the
  // chip had just put in the URL with the TRC default — invisible on TRC rows
  // because the default IS TRC. It now skips its first run when a rail came
  // from the URL; a later direction flip still swaps to the defaults.
  const railsFromUrl = useRef(chainRail !== null || !!urlTakeAsset || !!urlGiveAsset);
  const lastDirection = useRef(direction);
  useEffect(() => {
    if (railsFromUrl.current) { railsFromUrl.current = false; lastDirection.current = direction; return; }
    const d = direction === 'customer_buy'
      ? { take: DEFAULT_RAILS.cash,   give: DEFAULT_RAILS.hotTrc }
      : { take: DEFAULT_RAILS.hotTrc, give: DEFAULT_RAILS.cash   };
    const flipped = lastDirection.current !== direction;
    lastDirection.current = direction;
    if (flipped) {
      // A direction flip swaps to that direction's defaults, as always.
      setTakeRail(settleLater ? { slug: '', asset: d.take.asset } : { ...d.take });
      setGiveRail(settleLater ? { slug: '', asset: d.give.asset } : { ...d.give });
      return;
    }
    // A settle-later toggle alone keeps the ASSETS the operator picked (an
    // EUR/USDT pair stayed EUR/USDT — review, 14 Sept 2026): on, the drawers
    // go; off, each side takes its default drawer for the asset it holds,
    // else the direction's default.
    setTakeRail((r) => settleLater ? { slug: '', asset: r.asset || d.take.asset } : { ...d.take });
    setGiveRail((r) => settleLater ? { slug: '', asset: r.asset || d.give.asset } : { ...d.give });
  }, [direction, settleLater]);

  // ── VALIDATION — words before the round trip, from the payload's own state ─
  // The §6 words (item 72): "Pays and receives can't be the same account /
  // asset" — the legs are named on screen as pays / gets, never take / give.
  const sameRail = takeRail.slug === giveRail.slug && sameAsset(takeRail.asset, giveRail.asset) && takeRail.slug !== '';
  const sameAssetPair = !!takeRail.asset && sameAsset(takeRail.asset, giveRail.asset);
  const legsProblem = sameRail ? "Pays and receives can't be the same account."
    : sameAssetPair ? "Pays and receives can't be the same asset." : null;
  const pairKnown = !!takeRail.asset && !!giveRail.asset && !sameAssetPair;
  const lastTen: LastTenProps = { kind: 'trades', direction, pair: pairKnown ? { base: math.base, quote: math.quote } : undefined,
    partySlug: partyExists ? partyKey : undefined };
  const takeN = Number(amountTake) || 0, giveN = Number(amountGive) || 0, rateN = Number(rate) || 0;

  // THE RATE BOX (sweep item 21): the calculator cleans a pasted "3,67" or
  // "AED 3.672" on the way in; whatever is still not a plain decimal is named
  // here and Book stays off — the same sentence the BFF answers with, one
  // voice (lib/amount.ts, lib/errors.ts rateWords).
  const rateShape = rateProblem(rate);

  // SIDES SWAPPED (sweep item 40): a currency on the coin side and a coin on
  // the money side — the shape the BFF refuses as `sides_swapped` (Loki's EUR
  // conversion, J639D, 11 Sept). Said here, in the BFF's own words via
  // lib/errors.ts explain(), before any request; Book stays off. NOTHING is
  // flipped for the operator: they pick.
  const swapped = useMemo(() => {
    if (!pairKnown) return null;
    const coinSide  = direction === 'customer_buy' ? giveRail.asset : takeRail.asset;   // what the customer buys or sells
    const moneySide = direction === 'customer_buy' ? takeRail.asset : giveRail.asset;   // what it is priced in
    if (!isFiat(coinSide) || isFiat(moneySide)) return null;
    return explain(400, { error: 'sides_swapped', fiat: displayAsset(coinSide), coin: displayAsset(moneySide) });
  }, [pairKnown, direction, takeRail.asset, giveRail.asset]);

  // THE RATE BAND (sweep item 24), one table for every screen (lib/trade-math
  // .ts BAND, keyed by the oriented pair). A TYPED off-band rate turns Book
  // off until the operator presses the confirm that names the consequence;
  // a rate the calculator DERIVED from two typed amounts is only amber — the
  // amounts are the facts, and an odd rate between them is a question, not
  // a slip in the rate box.
  const bandLine = pairKnown && rateN > 0 && !rateShape ? bandWords(math.base, math.quote, rateN) : null;
  const bandTyped = !!bandLine && !math.rateIsDerived;
  // The confirm remembers WHICH rate it answered: retype the rate and it is
  // asked again.
  const [bandAck, setBandAck] = useState('');
  const bandPending = bandTyped && bandAck !== rate;

  // THE DESK'S OWN DEALT MEDIAN (sweep item 25): one line when this rate is
  // more than 1 % from what the desk dealt on this pair, this way, in the
  // last 24 hours. Book stays allowed — a rate can move; the line is there so
  // a 3.26 typed for 3.62 is seen before it is booked.
  const median = useQuery({
    queryKey: ['trades', 'median', entity, math.base, math.quote, direction],
    queryFn:  () => api.tradeMedian({ entity, base: math.base, quote: math.quote, direction }),
    enabled:  pairKnown && rateN > 0,
    staleTime: 60_000,
  });
  const medianRead = readState(median);
  const medianLine = useMemo(() => {
    const m = medianRead === 'known' ? median.data : undefined;   // a failed refresh keeps the old median beside the error, a retry has none: no claim from either
    if (!m || m.median === null || !(rateN > 0) || rateShape || !pairKnown) return null;
    if (Math.abs(rateN - m.median) / m.median <= 0.01) return null;
    // The median to four places — the one fmtRate, on a rounded figure. The pair and the way are the Rate label and
    // the Buys / Sells toggle above it, so the line says only what they cannot: the desk's figure and this one.
    return `desk average today ${fmtRate(Number(m.median.toFixed(4)), math.quote)} (${m.trades} trade${m.trades === 1 ? '' : 's'}) — this one is ${fmtRate(rateN)}`;
  }, [median.data, medianRead, rateN, rateShape, pairKnown, direction, math.base, math.quote]);

  // THE RATE READ BOTH WAYS (21 Oct 2026 audit) is the check for a rate typed
  // upside down, so it is shown wherever that check is still needed: a rate
  // outside its band, a pair the band table does not know (BTC, an unknown
  // coin), or a rate the desk's median flags. A rate plainly INSIDE its band is
  // the right way up by definition (the table is keyed by the oriented pair),
  // and the box's own label says which way it reads.
  const rateInBand = pairKnown && rateN > 0 && !rateShape && !!bandFor(math.base, math.quote) && !bandLine;
  const bothWays = pairKnown && rateN > 0 && !rateShape && (!rateInBand || !!medianLine);

  // THIS IS A CONVERSION (sweep item 27): settle-later ticked and the party's
  // box already holds what they would pay — the desk holds their EUR and the
  // trade has them "paying" EUR. The sweep would net it, which is exactly what
  // Convert balance does on purpose (U0RKO, JP, 11 Sept). Sign law: a
  // negative box is the desk holding theirs.
  const heldTake = useMemo(() => deskHolds(boxBalances, takeRail.asset), [boxBalances, takeRail.asset]);
  // Answered for THIS party, asset, amount and shape; any change asks again.
  const convSigFor = (later: boolean) => `${partyKey}|${takeRail.asset}|${amountTake}|${later ? 1 : 0}`;
  const convSig = convSigFor(settleLater);
  const [convAck, setConvAck] = useState('');
  // THE CONVERSION CHECK, for a trade of the given shape (lib/settle-later.ts conversionGateOf — one statement, read here and by the tests):
  // nothing in the way (null) — or the box already holds what they would pay and nobody has answered ('ask') — or something it stands on
  // has not been SUCCESSFULLY read ('unchecked'), the lookup first (a pending or failed lookup is not "a new name") and then the box.
  // `answered`: this very press is the answer to the question.
  const conversionGate = (later: boolean, answered = false): 'ask' | 'unchecked' | null => conversionGateOf({
    later, partyKey, takeN, lookup, partyExists, box, heldTake, dust: dustFor(takeRail.asset), answered, acknowledged: convAck === convSigFor(later),
  });
  const convGate = conversionGate(settleLater);
  const convPending = convGate === 'ask';
  const conversionUnchecked = convGate === 'unchecked';
  // The button says WHICH read it is standing on (the lookup, then the box) and whether it is still coming or failed.
  const uncheckedWords = lookup !== 'known'
    ? (lookup === 'failed' ? 'Party not read' : 'Reading party…')
    : (box === 'failed' ? 'Balance not read' : 'Reading balance…');

  // ── THE DATING LINE (sweep §4.3 ix / item 23, 15 Sept 2026) ──────────────
  // A settle-later trade dated Now for money that moved yesterday cannot be
  // settled by that money: the sweep draws on movements from a day before
  // the trade's instant onward, never earlier (Loki, 3 Sept). The party's
  // own statement — the same movements the sweep pools (a transfer, a split
  // payment, a receipt, a payment) — is read once the party is known, for
  // the last seven Dubai days, a minute stale. When settle-later is ticked,
  // both amounts are typed and the chips say Now, a live movement older than
  // 12 h that carries exactly one of this trade's legs (asset and amount,
  // either side) is named in one amber line with an offer to date the trade
  // to it. The EARLIEST such movement, deliberately: dated there, every
  // later one is inside the window too. A nudge, not a gate — Book stays
  // enabled; nothing is set without the tap.
  const weekAgo = dubaiDate(Date.now() - 7 * 86_400_000);
  const recentMoney = useQuery({
    queryKey: ['party', partyKey, 'recent-money', entity, weekAgo],
    queryFn:  () => api.partyStatement(partyKey, { entity, from: weekAgo, limit: 500 }),
    enabled:  partyExists,
    staleTime: 60_000,
  });
  const recent = readState(recentMoney);
  // Is the dating check RUNNING (the same conditions it has always had)? When it is and its read failed, the line below says it was not
  // done — a check that could not run is not a check that found nothing.
  const datingWanted = settleLater && partyExists && when === '' && takeN > 0 && giveN > 0;
  const dating = useMemo(() => {
    if (!datingWanted) return null;
    const rows = recent === 'known' ? (recentMoney.data?.rows ?? []) : [];   // a failed refresh keeps the old rows beside the error, a retry has none: no nudge from either
    const now = Date.now();
    const cutoff = now - 12 * 3_600_000;
    const MONEY = new Set(['transfer', 'party_payment_split', 'receipt', 'payment']);
    const legs = [{ asset: takeRail.asset, amount: takeN }, { asset: giveRail.asset, amount: giveN }];
    let pick: { at: string; reference: string | null } | null = null;
    for (const r of rows) {
      if (r.state !== 'live' || !r.at || !MONEY.has(r.kind)) continue;
      const t = Date.parse(r.at);
      if (!Number.isFinite(t) || t > cutoff) continue;
      // Exactly this leg: the statement's delta is the amount as it moved
      // the box, signed; the leg is unsigned. Float noise only.
      if (!legs.some((l) => sameAsset(r.asset, l.asset) && Math.abs(Math.abs(r.delta) - l.amount) < 1e-6)) continue;
      if (backdateProblem(isoToDubaiWall(r.at))) continue;
      if (!pick || t < Date.parse(pick.at)) pick = { at: r.at, reference: r.reference };
    }
    return pick && { ...pick, hours: Math.round((now - Date.parse(pick.at)) / 3_600_000) };
  }, [datingWanted, takeN, giveN, takeRail.asset, giveRail.asset, recentMoney.data, recent]);

  // THE READBACK (sweep item 14) — the trade in words, from the same fields
  // the payload reads, once both amounts exist: "Loki OTC pays 8,091 USDT·TRC
  // and receives 29,694 AED". The legs above carry the same name.
  //
  // THE NAME, NEVER THE SLUG (14 Sept 2026): opened with ?party=<slug> from a
  // party page or a Chain row, the leg labels, the readback and the balance
  // line read "loki-otc pays" until the party was retyped. The display name
  // lands the moment the parties query resolves (partyName on the record);
  // until then the slug shows muted and switches by itself.
  const nameKnown = !!selectedParty;
  const partyWords = selectedParty ? partyName(selectedParty) : partySlug;
  const who = partyWords || 'Customer';
  // THE LEG LABELS NAME ONLY A PARTY THE BOOKS KNOW (21 Oct 2026 audit): while the
  // name was still being typed they read "ha pays / ha gets" — the fragment, not a
  // party. Until the typed text IS a known party the labels use the screen's own
  // neutral word. The readback and the success line keep `who`: by then a name has
  // been typed on purpose (a new party's) and the sentence is the one being booked.
  const legWho = selectedParty ? partyWords : 'Customer';
  const readback = pairKnown && takeN > 0 && giveN > 0 && !!partySlug;
  // THE TOGGLE NAMES THE COIN (§6 trade direction): "Buys USDT / Sells USDT"
  // — the base of the oriented pair is what is bought or sold (USDT in
  // USDT/AED, EUR in EUR/AED, AED in AED/INR), read from the picked accounts.
  const coinWord = pairKnown ? displayAsset(foldAsset(math.base)) : 'USDT';

  // THE CHAIN ROW NUDGE (sweep item 16): the toggle disagrees with the row.
  const chainNudge = chainDir && chainDir !== direction
    ? (chainDir === 'customer_sell'
        ? 'Coins IN to the hot wallet — that is Customer sells.'
        : 'Coins OUT of the hot wallet — that is Customer buys.')
    : null;

  // Map workflow's payment_method ('bank-transfer') to BFF delivery_method ('bank_transfer')
  const PM_TO_DM: Record<string, 'cash' | 'bank_transfer' | 'crypto' | undefined> = {
    cash: 'cash', 'bank-transfer': 'bank_transfer', crypto: 'crypto', other: undefined,
  };

  // Lazy create flow — BFF returns 409 when partySlug doesn't exist, operator
  // picks one of three paths (Tracked / Treasury-only / Merchant — New
  // party's words, item 78), then mutation re-POSTs with that choice.
  type LazyChoice =
    | { kind: 'customer'; profile_kind: 'tracked' | 'treasury_only' }
    | { kind: 'merchant' };
  /** What a submit carries besides the form: an onboarding choice, or the
   *  repeat panel's "Book it settle-later instead" (sweep item 39, 14 Sept
   *  2026). The remedy flips the settle-later switch AND rides in the
   *  variables, because the re-POST it fires reads the closure of the render
   *  it was pressed in — where the switch still says settled. A ref would do
   *  the same job less visibly; the variables also survive a "post it again
   *  anyway" that follows, which re-posts `submit.variables`. */
  type SubmitVars = LazyChoice | { kind: 'settle_later' } | undefined;
  const [pendingProfileChoice, setPendingProfileChoice] = useState<null | { slug: string; display_name: string }>(null);


  // THE DEAL ID IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY.
  //
  // It used to be minted inside buildBody(), which runs on EVERY submit — so a
  // booking that timed out after the BFF had already posted came back as a
  // failure, the operator pressed Book again, and the second attempt carried a
  // NEW deal_id. The ledger dedupes on `reference` and the deals table on
  // (deal_id) DO NOTHING, so a stable id makes the retry a no-op — a fresh one
  // defeats both and books the trade twice. The lazy-party path made it worse:
  // a 409 needs_confirm is a DESIGNED re-POST, and it re-minted the id too.
  //
  // Held in a ref, not state: it must not re-render, and it must not reset when
  // the operator edits a field mid-retry. Cleared on success, so the next trade
  // gets its own.
  const dealIdRef = useRef<string | null>(null);
  // REPEAT GUARD: the twin the books already hold, and the operator's "book
  // it again anyway" — a ref, like the deal id, so it survives the re-POST and
  // clears on success. Keyed by WHICH guard refused (5 Sept 2026): the BFF
  // takes `confirm_repeat` for the twin-trade guard alone and
  // `confirm_money_moved_again` for the cross-shape guard alone, so a yes to
  // one no longer silences the other. A trade can meet both in turn — refused
  // as a twin, confirmed, then refused because its money already moved — and
  // the set keeps both, so the last re-POST carries both flags.
  // The guard, the panel and the confirm are shared with every other posting
  // screen (components/RepeatPanel.tsx). This screen kept its own copy until
  // 3 Sept, which is why the new `money_already_recorded` refusal — the one
  // that catches a settled trade re-recording money already paid — had no panel
  // here, the very screen it fires on. Since 14 Sept the same panel also
  // carries the MIRROR twin (sweep item 15): the BFF sends the sentence in
  // `twin.reason` and the panel prints it as it is.
  // The set also remembers the NEW-RAIL door (14 Sept 2026, evening): "yes,
  // open this asset on this rail" is a third, separate answer, and the BFF
  // asks its doors in turn on every POST, so a re-POST has to carry every
  // yes given so far or an earlier door refuses again (Record money's rule).
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());

  const buildBody = (choice?: SubmitVars) => {
    // The id carries the day the trade HAPPENED — DEAL-2026-09-03-… for a
    // 3 Sept trade typed on the 5th — so it reads and sorts with that day's
    // entries. onWhenChange re-mints it when the date changes (5 Sept 2026).
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    // Settle-later: the switch, or the repeat panel's remedy riding in the
    // variables (see SubmitVars) — the same shape either way.
    const later = laterFor(choice);
    // No rail, no method: an unsettled trade was not delivered by cash or bank
    // or crypto, it was not delivered at all. Guessed here it would go into the
    // books as a delivery that never happened.
    const method = later ? undefined : derivePaymentMethod(takeRail.slug);
    return {
      // The party the guards above checked IS the party the books get: the same normalization the BFF applies (partyKeyOf), sent as the key.
      entity, direction, party_slug: partyKey,
      party_display_name: partySlug,        // operator can edit later via Party Detail
      profile_kind: choice?.kind === 'customer' ? choice.profile_kind : undefined,
      lazy_party_type: choice?.kind === 'merchant' ? 'counterparty' as const : undefined,
      // Two shapes, and the payload says which. A settled trade names both
      // drawers; an unsettled one names neither and carries `unsettled` instead
      // — the BFF REFUSES a rail alongside it rather than ignoring the field,
      // because "nothing moved" and "it moved from here" cannot both be true.
      ...(later
        ? { unsettled: true as const }
        : {
            treasury_take_slug: takeRail.slug,
            treasury_give_slug: giveRail.slug,
            delivery_method: method ? PM_TO_DM[method] : undefined,
            payment_method: method,           // legacy field, kept for back-compat
          }),
      // The amounts and the rate are the calculator's (math.amountOf per rail
      // asset, math.rate) — typed or derived, the books get what is on screen.
      amount_take: { asset: takeRail.asset, amount: Number(amountTake) },
      amount_give: { asset: giveRail.asset, amount: Number(amountGive) },
      rate,
      // payment_method_detail is gone from the payload (§5.1, 14 Sept 2026:
      // never sent in 157 trades — it was always undefined, so the wire is
      // unchanged).
      notes: notes || undefined,
      deal_id: dealId,
      confirm_repeat:            confirmedRef.current.has('repeat') || undefined,
      confirm_money_moved_again: confirmedRef.current.has('money')  || undefined,
      // The operator said yes to opening this asset on this rail (the
      // new-rail panel above the button).
      allow_new_rail_asset:      confirmedRef.current.has('new_rail') || undefined,
      // Only when set. Absent means now; the BFF is never handed "now" as an
      // explicit instant it then has to bound-check against its own clock.
      effective_at: whenIso || undefined,
    };
  };

  // A FAILED READ IS NOT AN EMPTY RESULT. With the pickers empty, the screen
  // offered to create the party the operator had just typed — a duplicate of
  // one that already exists, invisible because the list never loaded. Said
  // above the Party field (§5.1), where the pickers are.
  const readError = lookup === 'failed' ? readWords(parties.error)
    : accounts === 'failed' ? readWords(treasury.error) : null;

  // The failed read says WHICH read, and what not to do on the strength of it (a duplicate party is one careless tap).
  const readWhat = lookup === 'failed' && accounts === 'failed' ? 'parties and accounts' : lookup === 'failed' ? 'parties' : 'accounts';
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): a picker would open on "No such account" and a way to create one.
  const accountsDown = accounts === 'failed' && !treasury.data;
  const retryReads = () => { if (lookup === 'failed') void parties.refetch(); if (accounts === 'failed') void treasury.refetch(); };

  const submit = useMutation({
    mutationFn: async (choice?: SubmitVars) => {
      const body = buildBody(choice);
      const data = await guard(async () => {
        try {
          return await api.bookTrade(body);
        } catch (e) {
          // A new party is this screen's own business; the repeat and
          // already-recorded refusals belong to the shared guard around it.
          const body = e instanceof ApiError && e.status === 409
            ? (e.body as { needs_confirm?: string; suggested?: { slug: string; display_name: string } }) : null;
          if (body?.needs_confirm === 'create_party' && body.suggested) {
            setPendingProfileChoice({ slug: body.suggested.slug, display_name: body.suggested.display_name });
            throw new Error('needs_confirm');
          }
          throw e;
        }
      });
      // WHAT WAS SENT RIDES BACK WITH THE ANSWER, so the success line reports
      // the instant, the id and the figures that were posted — never whatever
      // the fields hold by the time it renders (review, 5 Sept 2026; see
      // `booked`).
      return {
        data, dealId: body.deal_id, asOf: body.effective_at ?? null,
        who, pays: legWords(body.amount_take), gets: legWords(body.amount_give), paysLeg: body.amount_take, getsLeg: body.amount_give,
        bookedParty: body.party_slug,
        inr: sameAsset(body.amount_take.asset, 'INR') || sameAsset(body.amount_give.asset, 'INR'),
      };
    },
    onSuccess: ({ data, dealId, asOf, who: bookedWho, pays, gets, paysLeg, getsLeg, bookedParty, inr }) => {
      setBookedLegs({ pays: paysLeg, gets: getsLeg });
      tg()?.HapticFeedback.notificationOccurred('success'); setPendingProfileChoice(null); dealIdRef.current = null;
      clearTwin(); confirmedRef.current.clear();
      // A deduped answer means the books already held this deal id — a first
      // attempt whose response was lost — and kept ITS date; the date in this
      // request was ignored, so it must not be reported as booked.
      setBooked({ asOf, dealId: data.deal_id ?? dealId, who: bookedWho, pays, gets, deduped: !!data.deduped, partySlug: bookedParty, inr });
      // The date leaves with the trade it was typed for. Left in the field, it
      // rode into the next booking — the walk-in after Loki's 3 Sept trade.
      setWhen('');
      // THE FIGURES LEAVE WITH THE TRADE TOO (sweep item 14, 14 Sept 2026): the
      // amounts, the rate, the note and the two confirms clear; the party and
      // the direction stay, so the next trade with the same party is two
      // numbers away and a second tap on the button books nothing. A chain
      // note that stayed would also have tripped the 30-day chain-note twin
      // guard on the next booking.
      math.reset(); setNotes(''); setBandAck(''); setConvAck('');
      // Booked FROM a chain row (?note=chain …): fire the matcher NOW so the
      // pending row is gone before the operator looks back (operator, 2 Sept:
      // "make it auto vanish once trade booked and matched"). Fire-and-forget —
      // a failed sync just leaves the row for the screen's own auto-sync.
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  const rows = treasury.data?.rows ?? [];
  /** The onboarding choice currently being posted, so the pressed button can
   *  say so and the other two can go quiet. */
  const onboarding = submit.isPending ? submit.variables : undefined;

  // A RAIL THAT HAS NEVER HELD THIS ASSET (400 new_rail_asset — sweep item
  // 22, asked of a settled trade's rails since 14 Sept 2026). The books
  // refuse to open an account on their own; the operator opens it in words,
  // from the same panel Record money grew on 12 Sept ("how to?"). The refusal
  // names the asset; the rail is whichever side carries it — both sides
  // carrying it is the same-asset hard stop, so at most one does.
  const newRail = submit.isError && isNewRailRefusal(submit.error)
    ? (() => {
        const asset = String((submit.error as ApiError).body.asset ?? '');
        const rail = sameAsset(asset, giveRail.asset) && !sameAsset(asset, takeRail.asset) ? giveRail : takeRail;
        return { asset: asset || rail.asset, rail, holds: newRailHolds(submit.error) };
      })()
    : null;

  // THE DEAL ID CARRIES THE DAY, so a change of date must re-mint it — or a
  // 3 Sept trade whose date was corrected after a refusal keeps reading
  // DEAL-2026-09-05-…. The ref is kept (see dealIdRef) while the books MAY
  // already hold it: a posting in flight, or a last failure that was not
  // provably answered before the first ledger write. "Any 4xx" was that test
  // until the 5 Sept review showed trades.ts can refuse with a 400/409 AFTER
  // the delivery legs are in the ledger — chart_violation, an inventory
  // refusal, insufficient_funds — and re-minting there is how a trade gets
  // booked twice. lib/errors.ts nothingLanded() names the codes that do come
  // first, and our own sentinels. The confirmations and the twin go with the
  // id: they answered a guard centred on the OLD instant, and a re-POST at
  // the new one must meet the guard afresh.
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; confirmedRef.current.clear(); clearTwin(); }
  };

  // ── THE BUTTON ──────────────────────────────────────────────────────────
  // Hard stops: nothing books until they clear. Confirms (an unusual rate, a
  // conversion) keep Book off until their own button is pressed — a button
  // whose label names the consequence (§7 rule 11).
  const hardStop = submit.isPending || !partySlug || !rate || !amountTake || !amountGive ||
    !takeRail.asset || !giveRail.asset ||
    (!settleLater && (!takeRail.slug || !giveRail.slug)) ||
    sameRail || sameAssetPair || !!pendingProfileChoice ||
    // A back-date the BFF would refuse is refused here first.
    !!whenWords || !!rateShape || !!swapped;
  const bookDisabled = hardStop || bandPending || convPending || conversionUnchecked;
  // WHY REVIEW IS OFF, in a few words (Record's rule; Screens' audit, 27 Oct 2026: a greyed Review with no reason) — the
  // first unmet condition above that no line of its own already says. The others (same account, a back-date, the rate's
  // shape, the swapped pair, the open question) carry their own line on the form.
  const missing = !partySlug ? 'Pick a party.'
    : !amountTake || !amountGive ? 'Enter both amounts.'
    : !rate ? 'Enter the rate.'
    : !takeRail.asset || !giveRail.asset || (!settleLater && (!takeRail.slug || !giveRail.slug)) ? 'Pick both accounts.'
    : null;
  // MAY THIS DOOR POST? ONE answer for every door that books — the Book button, the rate band's "Book anyway", the conversion's
  // "Continue", the new-account "Yes", the create-party buttons and BOTH repeat-panel doors — read from the shape the door is about
  // to send (laterFor: the switch AND the submit's own choice), never from the button's state alone. A booking whose conversion check
  // is open — asked, or the box unread — does not go out, whichever door it came through. The only `submit.mutate` is below.
  const book = (choice?: SubmitVars, opts: { answered?: boolean } = {}) => {
    if (conversionGate(laterFor(choice), opts.answered)) return;
    submit.mutate(choice);
  };
  const confirmBand = () => { setBandAck(rate); book(undefined); };
  const confirmConversion = () => { setConvAck(convSig); if (!bandPending) book(undefined, { answered: true }); };
  // After a booking the figures are gone and the button says so.
  const bookedAndCleared = submit.isSuccess && !amountTake && !amountGive && !rate;
  // The readback, unless something is still missing (that reason wins, as before) or the pair reads swapped.
  const showReadback = !(missing && !submit.isPending && !bookedAndCleared) && readback && !swapped;
  // ── end of the verbatim block ─────────────────────────────────────────────────────────────────────────────────────

  // REVIEW (Astra's primary, "Review trade"): the first press shows the trade once more beside the one irreversible
  // Book trade; nothing is posted by it. It is only a view — the Book button's own rules (bookDisabled, `book`) are the
  // gate, unchanged. Any change to what would be posted closes the review, so a Book press always books what was read.
  const lastTenTitle = `Last 10 ${direction === 'customer_buy' ? 'buys' : 'sells'}`
    + (pairKnown ? ` of ${displayAsset(foldAsset(math.base))}` : '')
    + (partyExists && selectedParty ? ` with ${partyName(selectedParty)}` : '');
  const [reviewing, setReviewing] = useState(false);
  // The readback is drawn once Review trade is pressed — never before it, where it only repeated the fields above (Astra,
  // 1 Nov 2026). Before it, the foot says only what is missing.
  const readbackShown = reviewing && showReadback;
  const whyNot = missing && !submit.isPending && !bookedAndCleared ? missing : null;
  useEffect(() => { setReviewing(false); },
    [partyKey, direction, takeRail.slug, takeRail.asset, giveRail.slug, giveRail.asset, amountTake, amountGive, rate, settleLater, when, notes]);

  const bookWords = `Book trade${settleLater ? ' · settle later' : ''}${whenIso && !whenWords ? ` · as of ${fmtDay(whenIso)}` : ''}`;
  const reviewWords = submit.isPending ? 'Booking…'
    : bookedAndCleared ? 'Book another'
    // The only thing between the operator and the button is a box nobody could read: say so on the button.
    : conversionUnchecked && !hardStop && !bandPending ? uncheckedWords
    : 'Review trade';
  const primary = `${repeat ? 'btn-secondary' : 'btn-primary'} w-full px-5 lg:w-auto`;

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what was BOOKED — who, both
     legs, the day, the deal id — with "Book another" back to a fresh form and the INR doors when a leg was INR. */
  const receipt = submit.isSuccess && booked ? (
    <Receipt
      title="Trade booked"
      deduped={booked.deduped}
      already="Already booked"
      movements="/trades"
      movementsLabel="Trades"
      reference={booked.dealId}
      facts={[
        { label: 'With', value: booked.who },
        { label: 'Customer pays', value: bookedLegs ? <ReceiptAmount value={bookedLegs.pays.amount} asset={bookedLegs.pays.asset} /> : booked.pays },
        { label: 'Customer receives', value: bookedLegs ? <ReceiptAmount value={bookedLegs.gets.amount} asset={bookedLegs.gets.asset} /> : booked.gets },
        { label: 'As of', value: postedAsOf(booked.asOf, fmtDay, submit.submittedAt) },
      ]}
      another={{ label: 'Book another', onClick: () => submit.reset() }}
      links={booked.inr
        ? <TradeChargeLinks dealId={booked.dealId} partySlug={booked.partySlug} />
        : <Link to={`/trades?party=${encodeURIComponent(booked.partySlug)}`} className={TAP}>Trades with them →</Link>}
    />
  ) : null;

  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:gap-x-6">
      <div className="xl:col-span-2"><PageHead title="Book trade" /></div>

      <div className="min-w-0">
        {readError && (
          <Notice action={<button type="button" className={TAP} onClick={retryReads}>Try again</button>}>
            {`Couldn’t read ${readWhat} — ${lookup === 'failed' ? 'don’t create a party yet' : 'try again'}.`}
          </Notice>
        )}
        {listBalancesFlagged && (
          <Notice action={<button type="button" className={TAP} onClick={() => void parties.refetch()}>Try again</button>}>
            Balances not read — try again.
          </Notice>
        )}

        <Panel>{receipt ?? (<>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-x-4 lg:gap-y-5">
            <Field label="Party">
              <Combobox
                value={partySlug}
                onChange={setPartySlug}
                options={partyOptions}
                more={rosterCut(parties.data)}
                placeholder="Search party"
                loading={parties.isFetching || lookup === 'waiting'}
                emptyHint={lookup === 'failed' ? 'Parties could not be read.' : NO_PARTY_CREATE_HINT}
              />
              {/* The box in words, in the assets on this trade — only from a KNOWN read. */}
              {partyExists && (box === 'failed' ? (
                <StateLine tone="warn" className="mt-1" more={readWords(partyBalances.error)}
                  action={<button type="button" className={TAP} onClick={() => void partyBalances.refetch()}>Try again</button>}>
                  Couldn&rsquo;t read the balance.
                </StateLine>
              ) : (
                box === 'known' ? <TradedBox balances={partyBalances.data?.balances} assets={[takeRail.asset, giveRail.asset]} />
                  : <p className="mt-1 text-meta text-tg-hint">Reading…</p>
              ))}
            </Field>

            <div className="min-w-0">
              <Toggle<Direction>
                label="Direction"
                value={direction}
                onPick={pickDirection}
                options={[
                  { id: 'customer_buy', words: <>Buys <b className="font-semibold">{coinWord}</b></> },
                  { id: 'customer_sell', words: <>Sells <b className="font-semibold">{coinWord}</b></> },
                ]}
              />
              {/* The chain row's own direction, while the toggle disagrees. Book stays allowed. */}
              <WarnLine>{chainNudge && `⚠ ${chainNudge}`}</WarnLine>
            </div>

            <Field label="Customer pays" hint={hintWords(lockedSide === 'take' && 'from chain', math.isDerived(takeRail.asset) && 'auto')}>
              <AmountBox
                label="Customer pays, amount"
                value={shownAmount(math.amountOf(takeRail.asset), takeRail.asset, math.isDerived(takeRail.asset), editing === 'take')}
                unit={takeRail.asset ? displayAsset(takeRail.asset) : ''}
                muted={math.isDerived(takeRail.asset)}
                locked={lockedSide === 'take'}
                onChange={(v) => { if (lockedSide !== 'take') math.setAmount(takeRail.asset, v); }}
                onFocus={() => setEditing('take')}
                onBlur={() => setEditing(null)}
              />
            </Field>
            {/* Unsettled: no account to name, only an asset. Every treasury account on both sides, the picker's own
                asset-aware filter, no automation. */}
            <Field label={settleLater ? 'Pays asset' : 'Receiving account'}>
              {settleLater ? (
                <AssetPicker value={takeRail.asset} onChange={(a) => setTakeRail({ slug: '', asset: a })} options={ASSETS} />
              ) : accountsDown ? (
                <AccountsDown value={takeRail} />
              ) : (
                <RailPicker balanceBelow value={takeRail} onChange={setTakeRail} rows={rows} partySlug={partyExists ? partyKey : undefined} partyEntity={entity}
                  loading={treasury.isFetching || accounts === 'waiting'} readFailed={accounts === 'failed'} placeholder="Pick an account" />
              )}
            </Field>

            <Field label="Customer receives" hint={hintWords(lockedSide === 'give' && 'from chain', math.isDerived(giveRail.asset) && 'auto')}>
              <AmountBox
                label="Customer receives, amount"
                value={shownAmount(math.amountOf(giveRail.asset), giveRail.asset, math.isDerived(giveRail.asset), editing === 'give')}
                unit={giveRail.asset ? displayAsset(giveRail.asset) : ''}
                muted={math.isDerived(giveRail.asset)}
                locked={lockedSide === 'give'}
                onChange={(v) => { if (lockedSide !== 'give') math.setAmount(giveRail.asset, v); }}
                onFocus={() => setEditing('give')}
                onBlur={() => setEditing(null)}
              />
            </Field>
            <Field label={settleLater ? 'Receives asset' : 'Paying account'}>
              {settleLater ? (
                <AssetPicker value={giveRail.asset} onChange={(a) => setGiveRail({ slug: '', asset: a })} options={ASSETS} />
              ) : accountsDown ? (
                <AccountsDown value={giveRail} />
              ) : (
                <RailPicker balanceBelow value={giveRail} onChange={setGiveRail} rows={rows} partySlug={partyExists ? partyKey : undefined} partyEntity={entity}
                  loading={treasury.isFetching || accounts === 'waiting'} readFailed={accounts === 'failed'} placeholder="Pick an account" />
              )}
            </Field>
            {legsProblem && <ErrorLine className="mt-0 lg:col-span-2">{legsProblem}</ErrorLine>}

            <Field label="Rate" hint={hintWords(!!math.rateWords && !sameAssetPair && math.rateWords, math.rateIsDerived && 'auto')}>
              <input
                className={`input font-mono tabular${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                aria-label="Rate"
              />
              {/* The rate's shape, the rate read both ways (only where it is the check still needed), the band (here
                  while its confirm is not open) and the desk's own median — each one line. */}
              <ErrorLine>{rateShape}</ErrorLine>
              {bothWays && (
                <p className="mt-1 text-meta text-tg-hint">
                  1 {displayAsset(math.base)} = {fmtRate(rateN, math.quote)} {displayAsset(math.quote)} · 1 {displayAsset(math.quote)} = {fmtRate(1 / rateN, math.base)} {displayAsset(math.base)}
                </p>
              )}
              <WarnLine>{bandLine && !(bandPending && !hardStop) && `⚠ ${bandLine}${math.rateIsDerived ? ' Worked out from the two amounts — check them.' : ''}`}</WarnLine>
              <WarnLine>{medianLine && `⚠ ${medianLine}`}</WarnLine>
              {pairKnown && rateN > 0 && (medianRead === 'failed' ? (
                <StateLine tone="warn" className="mt-1" action={<button type="button" className={TAP} onClick={() => void median.refetch()}>Try again</button>}>
                  Desk average not read.
                </StateLine>
              ) : medianRead === 'waiting' && (
                <StateLine className="mt-1">Reading the desk average…</StateLine>
              ))}
            </Field>
            {/* Settle later — merchants, and anyone already carrying a position (canSettleLater). */}
            {canSettleLater && (
              <Toggle<'now' | 'later'>
                label="Settlement"
                value={settleLater ? 'later' : 'now'}
                onPick={(v) => { laterTouched.current = true; setSettleLater(v === 'later'); }}
                options={[{ id: 'now', words: 'Settle now' }, { id: 'later', words: 'Settle later' }]}
              />
            )}
          </div>

          <div className="mt-5 border-t border-stroke pt-3">
            {/* When and Notes. Open on arrival when a link carried the time, so the date it brings is seen. */}
            <MoreDetails label="More" defaultOpen={!!urlWhen}>
              <AsOfChips preset={urlWhen || undefined} label="When" value={when} onChange={onWhenChange} disabled={submit.isPending} />
              <Field label="Notes">
                <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
              </Field>
            </MoreDetails>
          </div>

          {/* ── every answer, directly above the button ─────────────────────────────────────────────────────── */}
          <div className="mt-3 space-y-3 empty:hidden">
            {swapped && <ErrorLine className="mt-0">{swapped}</ErrorLine>}
            {/* An unusual TYPED rate: Review is off; this button books. */}
            {bandPending && !hardStop && (
              <StateLine tone="warn" more={bandLine}
                action={<button type="button" className={`${TAP} text-warn`} disabled={submit.isPending} onClick={confirmBand}>Book anyway</button>}>
                Unusual rate — check it.
              </StateLine>
            )}
            {/* A conversion: Convert balance settles it now; or continue as settle-later and let the sweep net it. */}
            {convPending && !hardStop && (
              <div className="space-y-2">
                <StateLine tone="warn" more="Convert balance settles it now. Continuing books it settle later, and the sweep nets it.">
                  You already hold <span className="whitespace-nowrap">{fmtAmount(heldTake, takeRail.asset)} {foldAsset(takeRail.asset)}</span> for them.
                </StateLine>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/parties/${encodeURIComponent(partyKey)}/convert`} className="btn-secondary px-3 text-body">Convert balance</Link>
                  <button type="button" className="btn-secondary px-3 text-body text-warn" disabled={submit.isPending} onClick={confirmConversion}>
                    Continue settle later
                  </button>
                </div>
              </div>
            )}

            {pendingProfileChoice && (
              <div className="space-y-2 rounded-lg border border-stroke bg-panel-2 p-3">
                {nearest.length > 0 && (
                  <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-meta text-tg-hint">
                    <span>Did you mean</span>
                    {nearest.map((o) => (
                      <button key={o.value} type="button" className="btn-link text-meta" disabled={submit.isPending}
                        onClick={() => { setPartySlug(o.value); setPendingProfileChoice(null); }}>
                        {o.label ?? o.value}
                      </button>
                    ))}
                    <span>?</span>
                  </p>
                )}
                <p className="text-body">
                  <span className="font-semibold">{pendingProfileChoice.display_name}</span> is new — create as:
                </p>
                <div className="grid grid-cols-[1fr_1.4fr_1fr] gap-2">
                  <LazyChoiceButton label="Tracked" hint="KYC required"
                    busy={onboarding?.kind === 'customer' && onboarding.profile_kind === 'tracked'}
                    disabled={submit.isPending || !!whenWords} onClick={() => book({ kind: 'customer', profile_kind: 'tracked' })} />
                  <LazyChoiceButton label="Treasury-only" hint="No KYC; name only"
                    busy={onboarding?.kind === 'customer' && onboarding.profile_kind === 'treasury_only'}
                    disabled={submit.isPending || !!whenWords} onClick={() => book({ kind: 'customer', profile_kind: 'treasury_only' })} />
                  <LazyChoiceButton label="Merchant" busy={onboarding?.kind === 'merchant'}
                    disabled={submit.isPending || !!whenWords} onClick={() => book({ kind: 'merchant' })} />
                </div>
                <button type="button" className={`${TAP} text-tg-hint disabled:opacity-40`} disabled={submit.isPending} onClick={() => setPendingProfileChoice(null)}>
                  Cancel
                </button>
              </div>
            )}

            {/* The new-account door: yes re-posts the same trade, same deal id, with allow_new_rail_asset. */}
            {newRail && (
              <div className="space-y-2">
                <StateLine tone="warn" more={`${newRail.holds.length ? `Holds ${newRail.holds.map(displayAsset).join(' / ')} today` : 'Never posted to'} — check asset and account first.`}>
                  That account has never held {displayAsset(newRail.asset)}.
                </StateLine>
                <button type="button" className="btn-secondary w-full px-3 text-body lg:w-auto" disabled={submit.isPending || !!whenWords}
                  onClick={() => { confirmedRef.current.add('new_rail'); book(submit.variables); }}>
                  Open {displayAsset(newRail.asset)} on {railName({ slug: newRail.rail.slug })} and book
                </button>
              </div>
            )}
            {repeat && (
              <RepeatPanel
                twin={repeat}
                pending={submit.isPending || !!whenWords || !!conversionGate(laterFor(submit.variables))}
                onAlreadyBooked={() => { clearTwin(); nav(-1); }}
                onPostAnyway={() => {
                  if (conversionGate(laterFor(submit.variables))) return;
                  confirmedRef.current.add(repeat.kind);
                  book(submit.variables);
                }}
                settleLater={canSettleLater
                  ? { onClick: () => { laterTouched.current = true; setSettleLater(true); book({ kind: 'settle_later' }); } }
                  : undefined}
              />
            )}
            {/* A refusal or a failure in the books' words — a 5xx or a lost answer MAY HAVE PARTLY POSTED and says so. */}
            {submit.isError && !isSentinel(submit.error) && !newRail && <ServerLine error={submit.error} />}


            {/* The dating nudge: this party's box already moved one of these legs over 12 h ago while the trade says Now. */}
            {datingWanted && (recent === 'failed' ? (
              <StateLine tone="warn" action={<button type="button" className={TAP} onClick={() => void recentMoney.refetch()}>Try again</button>}>
                Recent movements not read.
              </StateLine>
            ) : recent === 'waiting' && (
              <StateLine>Reading recent movements…</StateLine>
            ))}
            {dating && (
              <StateLine tone="warn"
                more={`${dating.reference ? `${dating.reference}. ` : ''}The sweep matches money to a trade by date: date the trade to when the money moved.`}
                action={<button type="button" className={TAP} disabled={submit.isPending} onClick={() => onWhenChange(isoToDubaiWall(dating.at))}>Set to then</button>}>
                Money moved {dating.hours} h ago — date it then?
              </StateLine>
            )}
          </div>

          {/* In review, the compact readback immediately before Book trade. Before it, only what is missing — a one-line note
              UNDER the button on the phone, centred, as on the other forms (FormFoot) — Screens' re-audit, 27 Oct 2026. */}
          <div className={`mt-5 flex ${readbackShown ? 'flex-col' : 'flex-col-reverse'} gap-3 border-t border-stroke pt-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6`}>
            {readbackShown
              ? <div className="min-w-0"><TradeReadback who={who} nameKnown={nameKnown} take={{ asset: takeRail.asset, amount: takeN }} give={{ asset: giveRail.asset, amount: giveN }} /></div>
              : whyNot ? <p className="min-w-0 text-center text-meta text-tg-hint lg:text-left">{whyNot}</p>
              : <span className="max-lg:hidden" />}
            {reviewing ? (
              <div className="flex shrink-0 flex-col-reverse gap-2 lg:flex-row">
                <button type="button" className="btn-secondary w-full px-4 lg:w-auto" disabled={submit.isPending} onClick={() => setReviewing(false)}>Edit</button>
                <button type="button" className={primary} disabled={bookDisabled} onClick={() => book(undefined)}>
                  {submit.isPending ? 'Booking…' : bookWords}
                </button>
              </div>
            ) : (
              <button type="button" className={`${primary} shrink-0`} disabled={bookDisabled} onClick={() => setReviewing(true)}>
                {reviewWords}{reviewWords === 'Review trade' && <ArrowRight className="h-4 w-4" aria-hidden />}
              </button>
            )}
          </div>
          <LastTenLink {...lastTen} title={lastTenTitle} />
          <AboutJob help={<TradeAbout />} className="xl:hidden" />
        </>)}</Panel>
      </div>

      {/* The Last 10 of this direction (and pair, and party) beside the form — restored 1 Nov 2026; one message when the
          party's read failed, as before. After a booking it is all the aside keeps (Astra). */}
      <div className="min-w-0 max-xl:hidden xl:space-y-6">
        {!(partyExists && box === 'failed') && (
          <LastTen {...lastTen} title={lastTenTitle} />
        )}
        {/* The pair, the settlement and the date are in the fields; beside them only the explanation (Astra, 1 Nov 2026). */}
        {!receipt && <AboutJob help={<TradeAbout />} />}
      </div>
    </div>
  );
}

/** Book trade's explanation — the laptop's aside and the phone's fold under the form say the same (1 Nov 2026). */
function TradeAbout() {
  return (
    <>
      <p>
        One exchange with one party: what they pay and what they get, each on the account it moved through. A staged
        deal — deposit today, seller and delivery later — is a{' '}
        <Link to="/mc" className="text-tg-link hover:underline">Manager&rsquo;s cheque</Link>.
      </p>
      <p>
        <strong className="font-medium text-tg-text">Settle later</strong> is for merchants, and for anyone already
        carrying a balance. It books the trade on their account and touches no desk account; record the money when it
        arrives. A merchant opens with it on, a customer off.
      </p>
      <p>
        Any two of the two amounts and the rate fill the third; the box marked auto is the one that moves. The rate
        reads the pair&rsquo;s way — AED per USDT, INR per AED.
      </p>
      <p>
        A name the books have not seen is created from the trade, as Tracked (KYC required), Treasury-only (no KYC;
        name only) or Merchant. The direction opens on the last trade booked with the party — a default, not a rule.
      </p>
    </>
  );
}
